import {
  GatewayControleQuinquennal,
  GatewayDeclarationActivite,
  GatewayQuestionnaire,
} from "@conformite/gateway";
import ChevronDownIcon from "@endya-conformite/ui-kit/src/assets/icons/chevron-down.svg?react";
import { QuestionnaireForm } from "@src/components/questionnaire/QuestionnaireForm/QuestionnaireForm";
import { useQuestionnaireLatestInstanceQuery } from "@src/data/questionnaire/hook/useQuestionnaireLatestInstanceQuery";
import { getRemediationStatus } from "@src/helper/remediation.helper";
import classNames from "classnames";
import { CSSProperties, useMemo, useState } from "react";
import { AxeStatusIcon } from "./AxeStatusIcon";
import styles from "./AxeCard.module.scss";
import { SubAxisVerificationBanner } from "./SubAxisVerificationBanner";

type AxeCardProps = {
  label: string;
  slug: GatewayQuestionnaire.QuestionnaireSlug | undefined;
  contextType: string;
  contextId: string;
  axeId: string;
  stickyTop: number;
  onOpen?: () => void;
  verification?: GatewayControleQuinquennal.ControleQuinquennalSubAxisVerification;
  onRelaunchVerification?: () => void;
  isRelaunchingVerification?: boolean;
  declaredIobspActivites?: GatewayDeclarationActivite.ActiviteIobsp[];
  disabled?: boolean;
};

export function AxeCard({
  label,
  slug,
  contextType,
  contextId,
  axeId,
  stickyTop,
  onOpen,
  verification,
  onRelaunchVerification,
  isRelaunchingVerification,
  declaredIobspActivites,
  disabled,
}: AxeCardProps) {
  const [isOpen, setIsOpen] = useState(false);
  const toggle = () => {
    const willOpen = !isOpen;
    setIsOpen(willOpen);
    if (willOpen) {
      onOpen?.();
    }
  };
  const { data: latestInstance } = useQuestionnaireLatestInstanceQuery(
    contextType,
    contextId,
    slug
  );
  const status = getRemediationStatus(latestInstance);

  // Même mécanique que CQAxeQuestionnaire : Map questionId → réponse IA,
  // consommée par QuestionnaireForm/AiSuggestion. Affichage seulement, aucune écriture.
  const aiAnswersByQuestionId = useMemo(() => {
    const answers = verification?.resultPayload?.answers;
    if (!answers || answers.length === 0) return undefined;
    return new Map(answers.map((answer) => [answer.questionId, answer]));
  }, [verification?.resultPayload]);

  return (
    <>
      <div data-axe-anchor aria-hidden className={styles.anchor} />
      <button
        type="button"
        data-axe-header
        className={styles.collapseRow}
        style={{ "--sticky-top": `${stickyTop}px` } as CSSProperties}
        onClick={toggle}
      >
        <div className={styles.collapseContent}>
          <div className={styles.collapseLabelWrapper}>
            <AxeStatusIcon status={status} />
            <p className={styles.collapseLabel}>{label}</p>
          </div>
          <ChevronDownIcon
            className={classNames(styles.collapseChevron, {
              [styles.collapseChevronOpen]: isOpen,
              [styles.collapseChevronClosed]: !isOpen,
            })}
          />
        </div>
      </button>

      <div
        className={classNames(styles.collapseFormWrapper, {
          [styles.collapseFormWrapperOpen]: isOpen,
        })}
      >
        <div className={styles.collapseFormContent}>
          {isOpen && (
            <>
              <SubAxisVerificationBanner
                verification={verification}
                onRelaunch={onRelaunchVerification}
                isRelaunching={isRelaunchingVerification}
              />
              <QuestionnaireForm
                slug={slug}
                contextType={contextType}
                contextId={contextId}
                hideHeader
                disabled={disabled}
                aiAnswersByQuestionId={aiAnswersByQuestionId}
                declaredIobspActivites={declaredIobspActivites}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
}
